import React, { useEffect, useState } from 'react';
import { TrendingUp, Users, Zap, CheckCircle } from 'lucide-react';
import { getDashboardOverview } from '../api/services';
import type { DashboardOverview } from '../api/services';
import './Dashboard.css';

export default function Dashboard() {
  const [overview, setOverview] = useState<DashboardOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const data = await getDashboardOverview();
      setOverview(data);
    } catch (err) {
      setError('Failed to load dashboard data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading dashboard...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  if (!overview) {
    return <div className="error">No data available</div>;
  }

  const metrics = [
    {
      title: 'Active Installations',
      value: overview.activeInstallations,
      icon: Users,
      color: 'primary',
    },
    {
      title: 'Tips Shown',
      value: overview.tipsShown,
      icon: Zap,
      color: 'warning',
    },
    {
      title: 'Helpful Rate',
      value: `${(overview.helpfulRate * 100).toFixed(1)}%`,
      icon: CheckCircle,
      color: 'success',
    },
    {
      title: 'Tutorial Completion',
      value: `${(overview.tutorialCompletionRate * 100).toFixed(1)}%`,
      icon: TrendingUp,
      color: 'info',
    },
  ];

  return (
    <div className="dashboard">
      <div className="page-header">
        <h2>Dashboard Overview</h2>
        <p>Real-time telemetry and analytics</p>
      </div>

      <div className="metrics-grid">
        {metrics.map((metric) => {
          const Icon = metric.icon;
          return (
            <div key={metric.title} className={`metric-card ${metric.color}`}>
              <div className="metric-header">
                <Icon className="metric-icon" size={24} />
              </div>
              <div className="metric-value">{metric.value}</div>
              <div className="metric-title">{metric.title}</div>
            </div>
          );
        })}
      </div>

      <div className="analytics-section">
        <div className="section-row">
          <div className="section-card">
            <h3>Session Metrics</h3>
            <div className="metric-row">
              <span>Active Sessions</span>
              <strong>{overview.sessions}</strong>
            </div>
            <div className="metric-row">
              <span>Tutorial Runs</span>
              <strong>{overview.tutorialRuns}</strong>
            </div>
            <div className="metric-row">
              <span>Step Completion Rate</span>
              <strong>{(overview.tutorialStepCompletionRate * 100).toFixed(1)}%</strong>
            </div>
            <div className="metric-row">
              <span>Hotspot Interaction Rate</span>
              <strong>{(overview.hotspotInteractionRate * 100).toFixed(1)}%</strong>
            </div>
          </div>

          <div className="section-card">
            <h3>Top Applications</h3>
            {overview.topApps.length > 0 ? (
              <div className="app-list">
                {overview.topApps.map((app) => (
                  <div key={app.appName} className="app-item">
                    <span className="app-name">{app.appName}</span>
                    <span className="app-count">{app.count}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="empty-text">No application data available</p>
            )}
          </div>
        </div>

        <div className="section-row">
          <div className="section-card">
            <h3>Failure Reasons</h3>
            {overview.topFailureReasons.length > 0 ? (
              <div className="failure-list">
                {overview.topFailureReasons.map((reason) => (
                  <div key={reason.reason} className="failure-item">
                    <span className="failure-reason">{reason.reason}</span>
                    <span className="failure-count">{reason.count}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="empty-text">No failure data available</p>
            )}
          </div>

          <div className="section-card">
            <h3>Client Versions</h3>
            {overview.activeInstallationsByVersion.length > 0 ? (
              <div className="version-list">
                {overview.activeInstallationsByVersion.slice(0, 5).map((ver) => (
                  <div key={ver.version} className="version-item">
                    <span className="version-name">v{ver.version}</span>
                    <div className="version-bar">
                      <div
                        className="version-fill"
                        style={{
                          width: `${(ver.count / Math.max(...overview.activeInstallationsByVersion.map((v) => v.count))) * 100}%`,
                        }}
                      />
                    </div>
                    <span className="version-count">{ver.count}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="empty-text">No version data available</p>
            )}
          </div>
        </div>
      </div>

      <div className="footer-text">
        <p>Last updated: {new Date(overview.dataFreshness).toLocaleString()}</p>
      </div>
    </div>
  );
}
